import { useState } from "react"
import type { IDPE } from "@/types/explore"

const DPE_COLORS: Record<string, string> = {
  A: "rgb(0, 150, 57)",
  B: "rgb(81, 181, 52)",
  C: "rgb(203, 215, 31)",
  D: "rgb(255, 234, 0)",
  E: "rgb(242, 148, 0)",
  F: "rgb(234, 80, 0)",
  G: "rgb(215, 25, 28)",
}

const GES_COLORS: Record<string, string> = {
  A: "rgb(163, 137, 212)",
  B: "rgb(130, 100, 190)",
  C: "rgb(105, 70, 170)",
  D: "rgb(80, 45, 145)",
  E: "rgb(60, 25, 120)",
  F: "rgb(45, 15, 100)",
  G: "rgb(30, 5, 75)",
}

const HIDDEN_KEYS = new Set(["_geopoint", "_id", "_i", "_score", "_rand", "longitude", "latitude"])

function formatDate(date: string | undefined): string | null {
  if (!date) return null
  const d = new Date(date)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString("fr-FR")
}

function buildAddress(dpe: IDPE): string | null {
  return (
    dpe.adresse_ban ||
    [dpe.numero_voie_ban, dpe.nom_rue_ban, dpe.code_postal_ban, dpe.nom_commune_ban].filter(Boolean).join(" ") ||
    null
  )
}

function formatValue(value: unknown): string | null {
  if (value == null || value === "") return null
  if (typeof value === "number") return value.toLocaleString("fr-FR")
  if (typeof value === "string") return value
  return JSON.stringify(value)
}

const FIELD_LABELS: Record<string, string> = {
  type_batiment: "Type de batiment",
  annee_construction: "Annee de construction",
  periode_construction: "Periode de construction",
  surface_habitable_logement: "Surface habitable",
  nombre_niveau_logement: "Niveaux",
  hauteur_sous_plafond: "Hauteur sous plafond",
  type_installation_chauffage: "Chauffage",
  type_energie_principale_chauffage: "Energie chauffage",
  type_installation_ecs: "ECS",
  type_energie_principale_ecs: "Energie ECS",
  conso_5_usages_par_m2_ep: "Conso. energie primaire",
  conso_5_usages_par_m2_ef: "Conso. energie finale",
  emission_ges_5_usages_par_m2: "Emissions GES",
  cout_total_5_usages: "Cout annuel estime",
  qualite_isolation_enveloppe: "Isolation enveloppe",
  qualite_isolation_murs: "Isolation murs",
  qualite_isolation_plancher_bas: "Isolation plancher bas",
  qualite_isolation_menuiseries: "Isolation menuiseries",
  indicateur_confort_ete: "Confort ete",
  type_ventilation: "Ventilation",
  numero_dpe: "N\u00B0 DPE",
  date_etablissement_dpe: "Date DPE",
  date_fin_validite_dpe: "Fin de validite",
  methode_application_dpe: "Methode",
}

const SUMMARY_FIELDS = [
  "type_batiment",
  "annee_construction",
  "surface_habitable_logement",
  "type_energie_principale_chauffage",
  "type_installation_chauffage",
  "type_energie_principale_ecs",
  "conso_5_usages_par_m2_ep",
  "emission_ges_5_usages_par_m2",
  "cout_total_5_usages",
  "qualite_isolation_enveloppe",
  "qualite_isolation_murs",
  "qualite_isolation_menuiseries",
  "indicateur_confort_ete",
  "type_ventilation",
]

interface DPEPopupProps {
  dpe: IDPE
}

const Row = ({ label, value, unit }: { label: string; value: string | null; unit?: string }) => {
  if (!value) return null
  return (
    <div className="flex justify-between py-0.5 border-b border-gray-100 gap-3">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-right text-gray-700 font-medium">
        {value}
        {unit ? ` ${unit}` : ""}
      </span>
    </div>
  )
}

export const DPEPopup = ({ dpe }: DPEPopupProps) => {
  const [showAll, setShowAll] = useState(false)
  const dpeClass = dpe.etiquette_dpe
  const gesClass = dpe.etiquette_ges
  const date = formatDate(dpe.date_etablissement_dpe)
  const address = buildAddress(dpe)

  const badgeStyle = (color: string, dark?: boolean): React.CSSProperties => ({
    backgroundColor: color,
    color: dark ? "#fff" : "#1f2937",
    padding: "1px 8px",
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  })

  // All fields for expanded view
  const allEntries = Object.entries(dpe as Record<string, unknown>)
    .filter(([key, value]) => !HIDDEN_KEYS.has(key) && value != null && value !== "")
    .map(([key, value]) => ({
      key,
      label: FIELD_LABELS[key] || key.replaceAll("_", " "),
      value: formatValue(value)!,
    }))

  return (
    <div style={{ maxWidth: 450, fontSize: 12, lineHeight: 1.5, paddingRight: 20 }}>
      {/* Header: DPE + GES badges */}
      <div className="flex items-center gap-1.5 mb-1.5">
        {dpeClass && (
          <span style={badgeStyle(DPE_COLORS[dpeClass] || "#ccc", ["F", "G"].includes(dpeClass))}>DPE {dpeClass}</span>
        )}
        {gesClass && <span style={badgeStyle(GES_COLORS[gesClass] || "#ccc", true)}>GES {gesClass}</span>}
      </div>

      {/* Date + Address */}
      {date && <div className="text-gray-500 mb-0.5">{date}</div>}
      {address && <div className="text-gray-400 text-[11px] mb-1.5">{address}</div>}

      {/* Summary or full details */}
      {!showAll ? (
        <div>
          {SUMMARY_FIELDS.map((key) => {
            const raw = (dpe as Record<string, unknown>)[key]
            const value = formatValue(raw)
            if (!value) return null
            const label = FIELD_LABELS[key] || key
            let unit: string | undefined
            if (key === "conso_5_usages_par_m2_ep" || key === "conso_5_usages_par_m2_ef") unit = "kWh/m\u00B2/an"
            if (key === "emission_ges_5_usages_par_m2") unit = "kgCO\u2082/m\u00B2/an"
            if (key === "surface_habitable_logement") unit = "m\u00B2"
            if (key === "cout_total_5_usages") unit = "\u20AC/an"
            if (key === "hauteur_sous_plafond") unit = "m"
            return <Row key={key} label={label} value={value} unit={unit} />
          })}
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowAll(true)
            }}
            className="mt-2 bg-transparent border-none text-blue-500 cursor-pointer text-xs p-0 font-medium"
          >
            Plus d'informations
          </button>
        </div>
      ) : (
        <div className="max-h-[350px] overflow-y-auto">
          {allEntries.map(({ key, label, value }) => (
            <Row key={key} label={label} value={value} />
          ))}
          <button
            onClick={(e) => {
              e.stopPropagation()
              setShowAll(false)
            }}
            className="mt-2 bg-transparent border-none text-blue-500 cursor-pointer text-xs p-0 font-medium"
          >
            Reduire
          </button>
        </div>
      )}
    </div>
  )
}
